import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { NAV } from '../../siteNav';
import { Logo } from './Logo';
import { useQuoteModal } from '../../contexts/QuoteModalContext';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const linkCls = 'text-[#f4efe6] text-[15px] font-medium tracking-wide hover:text-[#00b8ff] transition-colors duration-200';

function Dropdown({ item }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => !ref.current.contains(e.relatedTarget) && setOpen(false)}
    >
      <button
        type="button"
        className={`${linkCls} inline-flex items-center gap-1.5`}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        data-testid={`nav-${slug(item.label)}`}
      >
        {item.label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50">
          <ul className="min-w-[240px] rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl p-2 shadow-2xl" data-testid={`nav-menu-${slug(item.label)}`}>
            {item.children.map((c) => (
              <li key={c.to}>
                <Link
                  to={c.to}
                  className="block rounded-lg px-4 py-2.5 text-sm text-[#cfc8bb] hover:bg-white/5 hover:text-[#00b8ff] transition-colors duration-150"
                  data-testid={`nav-link-${slug(c.label)}`}
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MobileMenu({ onClose, onQuote }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="lg:hidden bg-black border-t border-white/10 h-[calc(100dvh-80px)] overflow-y-auto" data-testid="mobile-menu">
      <div className="px-6 py-6 flex flex-col gap-4">
        {NAV.map((item) => item.children ? (
          <div key={item.label}>
            <button
              type="button"
              onClick={() => setExpanded((e) => (e === item.label ? null : item.label))}
              className="w-full flex items-center justify-between text-lg text-[#f4efe6]"
              data-testid={`mobile-nav-${slug(item.label)}`}
            >
              {item.label}
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${expanded === item.label ? 'rotate-180' : ''}`} />
            </button>
            {expanded === item.label && (
              <div className="flex flex-col gap-3 pl-4 pt-3 border-l border-white/10 ml-1 mt-2">
                {item.children.map((c) => (
                  <Link key={c.to} to={c.to} onClick={onClose} className="text-[#cfc8bb] hover:text-[#00b8ff]" data-testid={`mobile-nav-link-${slug(c.label)}`}>
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Link key={item.to} to={item.to} onClick={onClose} className="text-lg text-[#f4efe6] hover:text-[#00b8ff]" data-testid={`mobile-nav-${slug(item.label)}`}>
            {item.label}
          </Link>
        ))}
        <button
          type="button"
          onClick={() => { onClose(); onQuote(); }}
          className="mt-2 inline-flex items-center justify-center bg-[#00b8ff] text-black rounded-full px-6 py-3 font-semibold"
          data-testid="mobile-production-quote-btn"
        >
          Get a Production Quote
        </button>
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled || mobileOpen ? 'bg-black/85 backdrop-blur-md' : 'bg-transparent'}`}>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" aria-label="Pusha Teez Home" data-testid="nav-home-logo">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-7 xl:gap-9" data-testid="main-nav">
          {NAV.map((item) => item.children ? (
            <Dropdown key={item.label} item={item} />
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `${linkCls} ${isActive ? 'text-[#00b8ff]' : ''}`}
              data-testid={`nav-${slug(item.label)}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openModal}
            className="hidden sm:inline-flex items-center bg-[#00b8ff] text-black rounded-full px-6 py-3 font-semibold text-[15px] hover:bg-[#f4efe6] transition-colors duration-200 whitespace-nowrap"
            data-testid="nav-production-quote-btn"
          >
            Get a Production Quote
          </button>
          <button className="lg:hidden text-[#f4efe6] p-2" onClick={() => setMobileOpen((o) => !o)} aria-label="toggle menu" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>
      {mobileOpen && <MobileMenu onClose={() => setMobileOpen(false)} onQuote={openModal} />}
    </header>
  );
}
